import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { hasCapability } from "@/lib/brokerAuth";
import {
  bondFlowOf,
  getBondSubmission,
  getResolvedBondConfig,
  BOND_FINAL_STEP_KEY,
  type BondConfig,
  type BondSubmission,
} from "@/lib/brokerBonds";
import { getAccount, type AccountPreapproval } from "@/lib/brokerAccounts";
import { toRenderFinalField } from "@/lib/bondFields";
import { sectionLeadText } from "@/lib/bondSubmissionProfile";
import {
  listCapacityProjects,
  type CapacityProject,
} from "@/lib/capacityRequest";
import { GIA_FOCUS } from "@/components/GiaSection";
import { GiaFormDialog } from "@/components/bond-submission/GiaFormDialog";
import { BidResultDialog } from "@/components/bond-submission/BidResultDialog";
import { ReportClaimDialog } from "@/components/bond-submission/ReportClaimDialog";
import { RequestRiderDialog } from "@/components/bond-submission/RequestRiderDialog";
import { issuedBondChoicesOfOrder } from "@/lib/bondServicing";
import { EditBondRequestDialog } from "@/components/bond-submission/EditBondRequestDialog";
import { useBondDocuments } from "@/lib/useBondDocuments";
import { messageFor } from "@/lib/errors";
import { PageHeader } from "@/components/PageHeader";
import { usePageTrailName } from "@/components/PageTrail";
import {
  BondWithdrawDialog,
  BondWithdrawnNotice,
} from "@/components/BondWithdraw";
import { isWithdrawn } from "@/lib/bondLifecycle";
import { readBondActions, readChangeRequest } from "@/lib/bondStateModel";
import {
  SubmittedBanner,
  SUBMITTED_META,
  WON_SUBMITTED_HEADING,
} from "@/components/SubmittedBanner";
import { REFERRAL_SENTENCE } from "@/components/ReferralNotice";
import {
  readSubmittedState,
  readWonSubmittedState,
} from "@/lib/submittedState";
import { wonSubmittedMessage } from "@/lib/wonBid";
import {
  Pill,
  SubmissionActionRow,
  SubmissionButtonRow,
} from "@/components/bond-submission/SubmissionShell";
import { RiderPendingNotice } from "@/components/bond-submission/RiderPendingNotice";
import { BondLifecycleTracker } from "@/components/bond-submission/BondLifecycleTracker";
import { SubmissionHistoryPanel } from "@/components/bond-submission/SubmissionHistoryPanel";
import { CurrentBondDetails } from "@/components/bond-submission/CurrentBondDetails";
import { SubmissionProgramTab } from "@/components/bond-submission/SubmissionProgramTab";
import { ProjectDetailsBox } from "@/components/bond-submission/ProjectDetailsSections";
import {
  SubmissionBillingPanel,
  SubmissionInvoicesBox,
} from "@/components/bond-submission/SubmissionBillingPanel";
import { SubmissionBondsPanel } from "@/components/bond-submission/SubmissionBondsPanel";
import { SubmissionDocumentsPanel } from "@/components/bond-submission/SubmissionDocumentsPanel";
import { SubmissionMessagesPanel } from "@/components/bond-submission/SubmissionMessagesPanel";
import { SubmissionMessageDialog } from "@/components/bond-submission/SubmissionMessageDialog";
import { requiredDocumentCount } from "@/lib/submissionDocuments";
import { RecordTabs } from "@/base/RecordTabs";
import { recordPanelId, recordTabId, useTabParam } from "@/base/tabParam";
import { buttonClass } from "@/components/buttonStyles";
import {
  SUBMISSION_TABS,
  backToAccountLabel,
  bidResultButton,
  giaButton,
  programChip,
  programEnrollmentButton,
  submissionActionRow,
  headerPills,
  DOCUMENTS_FOCUS,
  tabForFocus,
  type SubmissionRowAction,
  type SubmissionTabId,
} from "@/lib/submissionShell";
import { WELL } from "@/base";

// BRK-S140 — one bond order, as a RECORD.
//
// The list has always linked into the WIZARD, which is right for a draft and
// wrong for everything else: a filed order is not a form, and a broker opening
// SB-40711 to check what they submitted had to walk a stepper to read it. So
// `/surety/submissions/{id}` is now the profile, and the flow moved one segment
// down to `/surety/submissions/{id}/continue`.
//
// READ-FIRST, NOT READ-ONLY. A draft is a real state here — the pointer exists
// from draft start, so half the orders in the list are drafts — and it gets a
// **Resume** into the flow rather than a locked page. The one thing a SUBMITTED
// order can change from here is the bid result (BRK-S141), which is why the
// page owns the order in state rather than rendering a snapshot.
//
// ── THE BID-RESULT REMINDER'S DEEP LINK (BRK-S177 → BRK-S183) ───────────────
//
// PMS's `bid_result_needed` chaser (PMS-S464) links here with
// `?focus=bid-result`, and its "We won" / "We lost" links add `&result=`. Since
// BRK-S183 the bid result has its OWN screen, so this page REDIRECTS those links
// to `/surety/submissions/{id}/bid-result`, carrying a validated `?result=` as
// a preselection. Nothing is saved by following a link — the screen still asks
// the broker to confirm. The redirect keeps every link already sitting in a
// broker's inbox working.
//
// BOTH VALUES ARE VALIDATED against what this build offers rather than trusted:
// a deep link is a string somebody else composed, and `?result=maybe` must read
// as no suggestion at all rather than as a value the screen is asked to take.
//
// The GIA reminder (PMS-S486) follows the same precedent with `?focus=gia`
// (BRK-S182): the page reads the parameter and, since BRK-S423, opens the GIA
// Form dialog, where `GiaSection` lands the cursor once its signers have
// loaded. The bond packet notice (BRK-S188) does the same with
// `?focus=documents` on the Submission Documents tab.
//
// ── THE CONFIRMATION IS THIS PAGE (BRK-S278, design § 5.1) ─────────────────
//
// The wizard ends by navigating HERE, with `replace` and router state
// `{ submitted: true, referred }`, and this page shows a dismissible
// `SubmittedBanner` while that state is present. Router state rather than a
// query string, so a shared or bookmarked link never shows the banner, and
// dismissing it replaces the entry with `state: null`, so a reload does not.
// Since BRK-S502 the page is the October wireframe's and nothing else: the
// GIA is the GIA Form dialog's, Signal Advance the Surety Program tab's and
// the account page's, and there is no Required steps panel, referral notice
// or "Start another".
//
// ── NO HARDCODED FIELD LIST ──────────────────────────────────────────────────
//
// The sections are built by grouping the CONFIGURATION SNAPSHOT's own final-step
// fields (`bondProfileSections`), and each value is formatted from that field's
// declared type. So a field Greg adds to the configuration appears here with no
// portal release, and a field this build has never heard of lands in a named
// catch-all section rather than disappearing.
//
// ── THE SHELL (BRK-S399, BRK-S502) ──────────────────────────────────────────
//
// The October wireframe, for EVERY order (BRK-S500 serves every v2 block;
// there is no pre-v2 layout to fall back to): the way back and the Bond
// Lifecycle box above the header; the header's SB number, ONE status pill and
// the "insured · project · obligee" line; the button row of three (Program
// Enrollment, GIA Form, Bid Result) and the FIXED action row of six (Message
// Signal, Upload Documents, Edit Bond Request, Request Rider, Report A Claim,
// Withdraw Bond); then eight tabs held in `?tab=`, the Submission Profile
// stacking each other tab's box. Every action renders the order view's
// `actions{}` (PMS-S837) and none is decided here (#7) — see
// `src/lib/submissionShell.ts`. The inbox's `?focus=gia|documents` links open
// their dialog or tab, and `?focus=bid-result` still redirects to the bid
// result screen.

/**
 * The `?focus=` value that means "the bid-result panel" (BRK-S177).
 *
 * The one spelling of the deep link's contract on this side, matching PMS's
 * `bidResultUrl` (lib/notification-templates.ts). A constant rather than an
 * inline string, because it is half of an agreement with another repo.
 */
const BID_RESULT_FOCUS = "bid-result";

/** The `?result=` suggestions this build will preselect. A closed list, so a
 *  hand-edited or stale link can only ever mean one of the two or nothing. */
const BID_OUTCOMES = ["won", "lost"] as const;

/** The value bag the final step's terms live in. `final` is a RESERVED key
 *  carrying the WHOLE shared bag, so the final step's own fields are read out of
 *  it BY KEY (see `BondSubmission.values`). */
function finalValues(order: BondSubmission): Record<string, unknown> {
  return order.values?.[BOND_FINAL_STEP_KEY] ?? {};
}

/** What an account read that says nothing about a program means: `none` (the
 *  account page's own reading). */
const NO_PROGRAM: AccountPreapproval = {
  status: "none",
  programType: null,
  terms: null,
  evaluatedAt: null,
  expiresAt: null,
  indicationAvailable: false,
};

/** The account's program, as the Program Enrollment chip and the Surety
 *  Program tab read it: unread, unreadable, or what the account publishes. */
type AccountProgram = "loading" | "failed" | AccountPreapproval;

export function SuretySubmissionPage() {
  const { id = "" } = useParams();
  const { identity, authedRequest } = useAuth();
  const canViewAccount = hasCapability(identity, "account:view");
  // Filing a message is a write on the account's record: the account page's
  // own gate for its message dialog.
  const canMessage = hasCapability(identity, "account:edit");
  const location = useLocation();
  const navigate = useNavigate();
  // The wizard's hand-off (BRK-S278): present only on the navigation that
  // filed the order, never on a reload after dismissing, a bookmark or a link.
  const submitted = readSubmittedState(location.state);
  // The won-bid screen's hand-off (BRK-S184), on the same terms.
  const wonSubmitted = readWonSubmittedState(location.state);
  // The reminder's deep link (BRK-S177), read off the query string and
  // validated against what this build offers — `?result=maybe` is no
  // suggestion rather than a value handed to the control.
  const [params] = useSearchParams();
  const focusBidResult = params.get("focus") === BID_RESULT_FOCUS;
  // The GIA reminder's deep link (BRK-S182, PMS-S486), the same precedent.
  const focusGia = params.get("focus") === GIA_FOCUS;
  // The bond packet notice's deep link (BRK-S188), the same precedent.
  const focusDocuments = params.get("focus") === DOCUMENTS_FOCUS;
  const suggestedOutcome = BID_OUTCOMES.find((o) => o === params.get("result"));

  const [order, setOrder] = useState<BondSubmission | null>(null);
  // Bumped after a write that answers with something other than the order
  // view (a GIA write, a claim, a message, a rider), so the order is re-read
  // rather than inferred from one route's body.
  const [refresh, setRefresh] = useState(0);
  const [config, setConfig] = useState<BondConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accountProgram, setAccountProgram] =
    useState<AccountProgram>("loading");
  // The account's capacity-projects read (BRK-S415): this order's own open
  // allocation, so the aggregate meter's "other bonds" leaves it out. Null
  // while unread or when the read fails — the meter is then dropped.
  const [capacityProjects, setCapacityProjects] = useState<
    CapacityProject[] | null
  >(null);
  // The order's documents, read here for the Documents tab (BRK-S146).
  const {
    documents,
    error: documentsError,
    reload: reloadDocuments,
  } = useBondDocuments(authedRequest, id === "" ? undefined : id);

  const [tab, selectTab] = useTabParam(SUBMISSION_TABS, "tab");
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  // Bumped by the action row's "Upload Documents" so the Documents tab's
  // panel opens its upload dialog (BRK-S424).
  const [docsUploadRequest, setDocsUploadRequest] = useState(0);
  const [messageOpen, setMessageOpen] = useState(false);
  // Bumped when the header's dialog has sent (BRK-S425), so the Messages
  // tab's thread re-reads.
  const [messagesVersion, setMessagesVersion] = useState(0);
  // The GIA Form dialog (BRK-S423), open on arrival from the reminder's
  // `?focus=gia` link.
  const [giaOpen, setGiaOpen] = useState(focusGia);
  // The Bid Result dialog (BRK-S422), opened from the button row.
  const [bidOpen, setBidOpen] = useState(false);
  // The shared Report a claim dialog (BRK-S428), from the action row.
  const [claimOpen, setClaimOpen] = useState(false);
  // The Edit Bond Request dialog (BRK-S419), opened while `actions.edit` allows.
  const [editOpen, setEditOpen] = useState(false);
  // The Request Rider dialog (BRK-S420), opened while `actions.rider` allows.
  const [riderOpen, setRiderOpen] = useState(false);

  // The top bar's trail: the account the submission belongs to, then its
  // number (BRK-S399). Until the order is read, the route's own label.
  usePageTrailName(
    order?.number,
    order
      ? [
          { label: "Accounts", to: "/surety/accounts" },
          {
            label: order.accountLegalName ?? "Account",
            to: `/surety/accounts/${encodeURIComponent(order.accountId)}`,
          },
        ]
      : undefined,
  );

  // An inbox link (`?focus=gia|documents`) opens the tab that holds its
  // anchor, ONCE: the section then scrolls and focuses itself as before. A
  // `?tab=` in the same link wins.
  const focusTab = tabForFocus(params.get("focus"));
  const focusApplied = useRef(false);
  useEffect(() => {
    if (focusApplied.current || focusTab === null) return;
    focusApplied.current = true;
    if (params.get("tab") === null && focusTab !== SUBMISSION_TABS[0].id) {
      selectTab(focusTab);
    }
  }, [focusTab, params, selectTab]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fresh = await getBondSubmission(authedRequest, id);
        if (cancelled) return;
        setOrder(fresh);
        // The configuration is resolved from the order's SUBPRODUCT, the same
        // way resume does it (BRK-S088): the order view carries the values and
        // not the labels, and the labels are what make the bag readable.
        //
        // A failed config read is NOT fatal — the header, the timeline and the
        // program block are all readable without it, so it degrades to a record
        // with no term sections rather than to an error page (NN #7).
        try {
          const resolved = await getResolvedBondConfig(
            authedRequest,
            fresh.subproductId,
          );
          if (!cancelled) setConfig(resolved);
        } catch {
          if (!cancelled) setConfig(null);
        }
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, id]);

  // The ACCOUNT's program (BRK-S399): the order view names no program type,
  // so the Program Enrollment chip reads the account it belongs to. A failed
  // read says the program is unavailable — never that there is none.
  const accountId = order?.accountId;
  useEffect(() => {
    if (!accountId || !canViewAccount) {
      setAccountProgram("failed");
      return;
    }
    let cancelled = false;
    getAccount(authedRequest, accountId)
      .then((detail) => {
        if (!cancelled) setAccountProgram(detail.preapproval ?? NO_PROGRAM);
      })
      .catch(() => {
        if (!cancelled) setAccountProgram("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [authedRequest, accountId, canViewAccount]);

  // The orders holding capacity on the account (BRK-S415), for the Program
  // limits block. A filed order's only: a draft has no bond to measure.
  const filed = order !== null && order.status !== "draft";
  useEffect(() => {
    if (!accountId || !canViewAccount || !filed) return;
    let cancelled = false;
    listCapacityProjects(authedRequest, accountId)
      .then((projects) => {
        if (!cancelled) setCapacityProjects(projects);
      })
      .catch(() => {
        if (!cancelled) setCapacityProjects(null);
      });
    return () => {
      cancelled = true;
    };
  }, [authedRequest, accountId, canViewAccount, filed]);

  // A quiet re-read of the order after such a write. No loading state and no error page: the page already shows
  // the order the action answered with, and a failed re-read keeps it.
  useEffect(() => {
    if (refresh === 0) return;
    let cancelled = false;
    getBondSubmission(authedRequest, id)
      .then((fresh) => {
        if (!cancelled) setOrder(fresh);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [authedRequest, id, refresh]);

  // The reminder's one-click links land on the bid result SCREEN (BRK-S183).
  // After every hook, so the hook order never changes between renders.
  if (focusBidResult) {
    return (
      <Navigate
        replace
        to={`/surety/submissions/${encodeURIComponent(id)}/bid-result${
          suggestedOutcome ? `?result=${suggestedOutcome}` : ""
        }`}
      />
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="Bond submission" />
        <section className="flex flex-col gap-6">
          <p role="alert" className="text-sm text-brk-danger">
            {error}
          </p>
          {/* No order, so no account to go back to: the list it came from. */}
          <Link
            to="/surety/submissions"
            className="text-sm text-brk-accent hover:underline"
          >
            ← Back to submissions
          </Link>
        </section>
      </>
    );
  }

  if (!order) {
    return (
      <>
        <PageHeader title="Bond submission" />
        <p role="status" className="text-sm text-brk-dim">
          Loading bond submission…
        </p>
      </>
    );
  }

  // The shared final-field adapter (BRK-S430): the same field shape the
  // wizard and the edit dialogs draw, so a value reads as its control does.
  const fields = (config?.finalStep.fields ?? []).map(toRenderFinalField);
  const values = finalValues(order);
  const flow = bondFlowOf(order);
  const isDraft = order.status === "draft";
  // `actions.message` (PMS-S837) with the page's capability gate folded in.
  const messageAction = canMessage
    ? (readBondActions(order.actions)?.message ?? {
        enabled: false,
        reason: null,
      })
    : {
        enabled: false,
        reason: "You don’t have permission to message about this account.",
      };
  // The six actions (BRK-S502), Message Signal carrying the capability.
  const rowActions = submissionActionRow(order).map((a) =>
    a.key === "message" ? { ...a, ...messageAction } : a,
  );
  const changeRequest = readChangeRequest(order.changeRequest);
  const program = typeof accountProgram === "string" ? null : accountProgram;
  // "insured · project · obligee", each only when the record names it.
  const headline = [
    order.accountLegalName ?? "Insured not named",
    sectionLeadText(fields, values, "project"),
    sectionLeadText(fields, values, "obligee"),
  ].filter((part): part is string => part !== null);

  // The ONE way back (BRK-S318), to the ACCOUNT the submission belongs to
  // (BRK-S399); the wireframe places it above the lifecycle and at the foot.
  const backLink = (
    <Link
      to={`/surety/accounts/${encodeURIComponent(order.accountId)}`}
      className="self-start text-sm text-brk-accent hover:underline"
    >
      ‹ {backToAccountLabel(order.accountLegalName)}
    </Link>
  );

  // The Project Details box (BRK-S502), on the Profile and its own tab, with
  // the action row's own Edit Bond Request.
  const projectDetails = (
    <ProjectDetailsBox
      fields={config === null ? null : fields}
      values={values}
      changeRequest={order.changeRequest}
      recentChanges={order.recentChanges}
      flow={flow}
      accountId={order.accountId}
      edit={rowActions.find((a) => a.key === "edit")}
      onEdit={() => onAction("edit")}
    />
  );

  function dismissBanner() {
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }

  function onAction(key: SubmissionRowAction) {
    if (key === "message") {
      setMessageOpen(true);
      return;
    }
    if (key === "withdraw") {
      setWithdrawOpen(true);
      return;
    }
    if (key === "edit") {
      setEditOpen(true);
      return;
    }
    if (key === "docs") {
      selectTab("documents");
      setDocsUploadRequest((n) => n + 1);
      return;
    }
    if (key === "claim") {
      setClaimOpen(true);
      return;
    }
    if (key === "rider") setRiderOpen(true);
  }

  return (
    <>
      {/* The way back, then THE BOND LIFECYCLE above the header (BRK-S414,
          BRK-S502 — the October wireframe): the five phases PMS publishes in
          `lifecycle`, on every order, a draft's included. */}
      <div className="mb-4 flex min-w-0 flex-col gap-4">
        {backLink}
        <BondLifecycleTracker order={order} />
      </div>
      <PageHeader
        title={<span className="tabular-nums">{order.number}</span>}
        status={
          // ONE status pill, and a pending change beside it (BRK-S502).
          <span className="flex flex-wrap items-center gap-1.5">
            {headerPills(order).map((p) => (
              <Pill key={p.label} pill={p} />
            ))}
          </span>
        }
        meta={
          // "insured · project · obligee", each only when the record names
          // it (BRK-S399).
          <span className="break-words">{headline.join(" · ")}</span>
        }
      />
      <section className="flex min-w-0 flex-col gap-6">
        {/* The arrival confirmations sit above the tabs: they answer the
            action that brought the broker here, whichever tab a link opens. */}
        {submitted && (
          <SubmittedBanner
            number={order.number}
            message={
              submitted.referred || order.referred === true
                ? REFERRAL_SENTENCE
                : SUBMITTED_META
            }
            onDismiss={dismissBanner}
          />
        )}
        {/* The won-bid screen's hand-off (BRK-S184): the sentence is
            the one the PUT's `status` earned, carried in router state. */}
        {wonSubmitted && (
          <SubmittedBanner
            number={order.number}
            heading={WON_SUBMITTED_HEADING}
            message={wonSubmittedMessage(
              wonSubmitted.status,
              wonSubmitted.bondsRequested,
            )}
            onDismiss={dismissBanner}
          />
        )}
        {/* The button row of three and the fixed action row of six
            (BRK-S399, BRK-S502), on every order — a draft's greyed with
            PMS's reasons. */}
        <div className="flex min-w-0 flex-col gap-3">
          <SubmissionButtonRow
            program={programEnrollmentButton(
              accountProgram === "loading" ? "loading" : program,
              order,
            )}
            accountId={order.accountId}
            gia={giaButton(order)}
            onOpenGia={() => setGiaOpen(true)}
            bidResult={bidResultButton(order)}
            onOpenBidResult={() => setBidOpen(true)}
          />
          <SubmissionActionRow actions={rowActions} onSelect={onAction} />
        </div>

        {/* A CANCELLED order (BRK-S181) is kept, not deleted, and reads as what
          it is. No Resume: PMS refuses every write on it (ORDER_CANCELLED), so
          a way back into the flow would be a form that cannot be saved. */}
        {/* WITHDRAWN (BRK-S323): the date, the reason, and a new draft from
          it — the order itself is never reopened (rule 21). */}
        {isWithdrawn(order) && <BondWithdrawnNotice order={order} />}
        {order.status === "cancelled" && (
          <p role="status" className={`text-sm text-brk-text ${WELL}`}>
            This request was cancelled. It’s kept here for your records, and
            nothing more will happen on it.
          </p>
        )}

        {/* A DRAFT is a real state here — the pointer exists from draft start, so
          half the list is drafts — and the way on is the flow, not this page. */}
        {isDraft && (
          <div className={`flex flex-col items-start gap-3 ${WELL}`}>
            <p className="text-sm text-brk-text">
              This request isn’t filed yet — pick up where you left off.
            </p>
            <Link
              to={`/surety/submissions/${order.id}/continue`}
              className={buttonClass()}
            >
              Resume this request
            </Link>
          </div>
        )}

        {/* A rider waiting on underwriting (`changeRequest`, PMS-S837), with
            Withdraw request (BRK-S420); its 2xx is the order view. */}
        {changeRequest?.kind === "rider" && (
          <RiderPendingNotice order={order} onUpdated={setOrder} />
        )}

        <SubmissionTabs
          active={tab}
          onSelect={selectTab}
          // What Signal is waiting on, beside the Documents tab (BRK-S424).
          badges={{ documents: requiredDocumentCount(documents) }}
          panels={{
            // THE SUBMISSION PROFILE (BRK-S502): everything at a glance, in
            // the wireframe's order — the current bond and the program
            // limits, the project details, Bonds, the first five documents,
            // Invoices and History. Each box is the one its own tab shows.
            profile: (
              <>
                {/* A draft has filed no bond yet: no card and no limits. */}
                {!isDraft && (
                  <CurrentBondDetails
                    order={order}
                    programName={
                      program?.status === "approved"
                        ? programChip(program).label
                        : null
                    }
                    capacity={program?.capacity ?? null}
                    projects={capacityProjects}
                    onEdit={() => onAction("edit")}
                  />
                )}
                {projectDetails}
                <SubmissionBondsPanel
                  bonds={order.bonds}
                  accountId={order.accountId}
                  lifecycle={order.lifecycle}
                />
                <SubmissionDocumentsPanel
                  order={order}
                  documents={documents}
                  error={documentsError}
                  onChanged={reloadDocuments}
                  onViewAll={() => selectTab("documents")}
                />
                <SubmissionInvoicesBox order={order} />
                <SubmissionHistoryPanel history={order.history} />
              </>
            ),
            program: (
              // The program block, this submission against the program, the
              // counted submissions and the Signal Advance pitch (BRK-S416).
              <SubmissionProgramTab
                order={order}
                program={accountProgram === "loading" ? "loading" : program}
                capacityProjects={capacityProjects}
              />
            ),
            details: projectDetails,
            // One row per bond from the order view's `bonds[]` (BRK-S417).
            bonds: (
              <SubmissionBondsPanel
                bonds={order.bonds}
                accountId={order.accountId}
                lifecycle={order.lifecycle}
              />
            ),
            // Status, source, related bond and uploads (BRK-S424), on every
            // order: there is no older read-only section to fall back to.
            documents: (
              <SubmissionDocumentsPanel
                order={order}
                documents={documents}
                error={documentsError}
                onChanged={reloadDocuments}
                focus={focusDocuments}
                uploadRequest={docsUploadRequest}
              />
            ),
            // The order's thread with Signal and its composer (BRK-S425).
            messages: (
              <SubmissionMessagesPanel
                submissionId={order.id}
                message={messageAction}
                version={messagesVersion}
                // The order's History logs the message: re-read it.
                onSent={() => setRefresh((n) => n + 1)}
              />
            ),
            // The estimate, invoiced / paid / outstanding tiles and this
            // order's invoices (BRK-S426), from the account's invoice read.
            billing: <SubmissionBillingPanel order={order} />,
            // The event table over status moves and events (BRK-S427).
            history: <SubmissionHistoryPanel history={order.history} />,
          }}
        />

        {/* The foot repeats the way back (the wireframe draws it at the top
            and the foot): the same link, one destination. */}
        {backLink}
      </section>

      <BondWithdrawDialog
        order={order}
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        onWithdrawn={setOrder}
      />
      {/* The GIA Form dialog (BRK-S423): on every filed order, opened by the
          button row's GIA Form button or the reminder's `?focus=gia`. A write
          inside it re-reads the order, so the button follows the server. */}
      <GiaFormDialog
        order={order}
        open={giaOpen}
        focus={focusGia}
        onClose={() => setGiaOpen(false)}
        onChanged={() => {
          setRefresh((n) => n + 1);
        }}
      />
      {/* The Bid Result dialog (BRK-S422): the button row opens it while
          `actions.bidResult` allows. A lost write's 2xx replaces the order,
          so the page follows the server's answer. */}
      <BidResultDialog
        order={order}
        open={bidOpen}
        onClose={() => setBidOpen(false)}
        onUpdated={setOrder}
      />
      {/* Report a Claim (BRK-S428): the account menu's dialog (BRK-S412) over
          this order's issued bonds. The 2xx re-reads the order, so a history
          row is the server's, never added here. */}
      <ReportClaimDialog
        open={claimOpen}
        onClose={() => setClaimOpen(false)}
        bonds={issuedBondChoicesOfOrder(
          order,
          sectionLeadText(fields, values, "obligee"),
        )}
        submissionId={order.id}
        onReported={() => setRefresh((n) => n + 1)}
      />
      {/* Edit Bond Request (BRK-S419): mounted while open, so each opening
          starts from the order as it reads now; a 2xx replaces the order. */}
      {editOpen && (
        <EditBondRequestDialog
          order={order}
          fields={config?.finalStep.fields ?? null}
          onClose={() => setEditOpen(false)}
          onUpdated={setOrder}
          onOpenDocuments={() => {
            setEditOpen(false);
            selectTab("documents");
          }}
        />
      )}
      {/* Request Rider (BRK-S420): the shared rider dialog (BRK-S412) over
          this order's issued bonds, drawing its bond details. Mounted while
          open, so each opening starts from the order as it reads now; the
          201 IS the order view (PMS-S850), so the banner and markers are the
          server's answer. */}
      {riderOpen && (
        <RequestRiderDialog
          open
          onClose={() => setRiderOpen(false)}
          bonds={issuedBondChoicesOfOrder(
            order,
            sectionLeadText(fields, values, "obligee"),
          )}
          submissionId={order.id}
          order={order}
          fields={config?.finalStep.fields ?? null}
          onRequested={setOrder}
        />
      )}
      {/* Message Signal (BRK-S425, BRK-S502): the Messages tab's own
          composer posting to the order's thread; it closes on the 2xx and
          the tab's thread re-reads. */}
      {messageAction.enabled && (
        <SubmissionMessageDialog
          open={messageOpen}
          submissionId={order.id}
          number={order.number}
          onClose={() => setMessageOpen(false)}
          onSent={() => {
            setMessageOpen(false);
            setMessagesVersion((n) => n + 1);
            setRefresh((n) => n + 1);
          }}
        />
      )}
    </>
  );
}

/**
 * The eight tabs (BRK-S399, BRK-S502), in `?tab=` so a reload or a shared
 * link reopens the same tab (the default, Submission Profile, stays out of
 * the URL). The Submission Profile stacks the other tabs' boxes — the current
 * bond, Project Details, Bonds, the first five documents, Invoices, History;
 * each other tab shows its own box (Surety Program and Billing carry more).
 *
 * A panel mounts the first time its tab opens and then STAYS mounted, hidden,
 * as the account page's do: switching back never loses what was typed.
 */
function SubmissionTabs({
  active,
  onSelect,
  badges,
  panels,
}: {
  active: string;
  onSelect: (id: string) => void;
  badges?: Partial<Record<SubmissionTabId, number>>;
  panels: Record<SubmissionTabId, ReactNode>;
}) {
  const idBase = useId();
  const [visited, setVisited] = useState<ReadonlySet<string>>(
    () => new Set([active]),
  );
  // A tab opened from the URL (back/forward, a link) counts as visited too.
  useEffect(() => {
    setVisited((v) => (v.has(active) ? v : new Set(v).add(active)));
  }, [active]);

  return (
    <div className="flex min-w-0 flex-col">
      {/* Scrolls inside its own box at 375px rather than wrapping or pushing
          the page sideways (RecordTabs). */}
      <div className="flex min-w-0 border-b border-brk-border">
        <RecordTabs
          tabs={SUBMISSION_TABS}
          active={active}
          onSelect={onSelect}
          idBase={idBase}
          ariaLabel="Submission sections"
          badges={badges}
        />
      </div>
      {SUBMISSION_TABS.map((t) => (
        <div
          key={t.id}
          role="tabpanel"
          id={recordPanelId(idBase, t.id)}
          aria-labelledby={recordTabId(idBase, t.id)}
          hidden={t.id !== active}
          // `hidden` alone loses to `flex` (a utility outranks preflight's
          // `[hidden]` rule), which left a visited tab showing under the
          // active one: the display class follows the state (CollapsibleBox).
          className={`${t.id === active ? "flex" : "hidden"} mt-4 min-w-0 flex-col gap-6`}
        >
          {(t.id === active || visited.has(t.id)) && panels[t.id]}
        </div>
      ))}
    </div>
  );
}

/** A tab whose content has not been built yet: says so, and nothing else. */
